'use client';

import { useState, useCallback, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import Link from 'next/link';
import { signIn } from 'supertokens-auth-react/recipe/emailpassword';
import Session from 'supertokens-auth-react/recipe/session';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ensureSuperTokensFrontendInit } from '@/lib/auth/supertokens-client';

ensureSuperTokensFrontendInit();

export default function LoginPage() {
  const router = useRouter();
  const [tenantCode, setTenantCode] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('erp_tenant_code') || '';
    }
    return '';
  });
  const [tenantName, setTenantName] = useState('');
  const [tenantVerified, setTenantVerified] = useState(false);
  const [tenantError, setTenantError] = useState('');

  // 저장된 회사코드가 있으면 자동 검증
  useEffect(() => {
    if (tenantCode && !tenantVerified) {
      verifyTenant(tenantCode);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const verifyTenant = useCallback(async (code: string) => {
    const trimmed = code.trim();
    if (!trimmed) {
      setTenantName('');
      setTenantVerified(false);
      setTenantError('');
      return;
    }

    try {
      const res = await fetch(`/api/auth/verify-tenant?code=${encodeURIComponent(trimmed)}`);
      const body = await res.json();

      if (!body.success || !body.data?.exists) {
        setTenantName('');
        setTenantVerified(false);
        setTenantError('존재하지 않는 회사코드입니다.');
        return;
      }

      if (!body.data.isActive) {
        setTenantName(body.data.name);
        setTenantVerified(false);
        setTenantError('비활성화된 회사입니다. 관리자에게 문의하세요.');
        return;
      }

      setTenantName(body.data.name);
      setTenantVerified(true);
      setTenantError('');
    } catch {
      setTenantName('');
      setTenantVerified(false);
      setTenantError('회사코드 확인 중 오류가 발생했습니다.');
    }
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // 회사코드 미검증 시 즉시 검증 후 계속 진행
    if (!tenantVerified) {
      const trimmed = tenantCode.trim();
      if (!trimmed) {
        setError('회사코드를 입력해주세요.');
        return;
      }
      try {
        const res = await fetch(`/api/auth/verify-tenant?code=${encodeURIComponent(trimmed)}`);
        const body = await res.json();
        if (!body.success || !body.data?.exists) {
          setTenantError('존재하지 않는 회사코드입니다.');
          return;
        }
        if (!body.data.isActive) {
          setTenantName(body.data.name);
          setTenantError('비활성화된 회사입니다. 관리자에게 문의하세요.');
          return;
        }
        setTenantName(body.data.name);
        setTenantVerified(true);
        setTenantError('');
      } catch {
        setTenantError('회사코드 확인 중 오류가 발생했습니다.');
        return;
      }
    }

    setIsLoading(true);

    try {
      const response = await signIn({
        formFields: [
          { id: 'email', value: email },
          { id: 'password', value: password },
        ],
      });

      if (response.status === 'FIELD_ERROR') {
        const fieldError = response.formFields.find((f) => f.error);
        setError(fieldError?.error ?? '입력값을 확인해 주세요.');
      } else if (response.status === 'WRONG_CREDENTIALS_ERROR') {
        setError('이메일 또는 비밀번호가 올바르지 않습니다.');
      } else if (response.status === 'SIGN_IN_NOT_ALLOWED') {
        setError('로그인이 허용되지 않은 계정입니다.');
      } else if (response.status === 'OK') {
        // 로그인 성공 → DB에서 사용자의 실제 tenantCode와 입력값 비교
        // (세션 토큰의 캐시된 tenantCode가 아닌 DB 직접 조회)
        const memberRes = await fetch('/api/auth/verify-membership', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ tenantCode: tenantCode.trim() }),
        });
        if (!memberRes.ok || !(await memberRes.json()).data?.match) {
          await Session.signOut();
          setError('해당 회사에 등록되지 않은 계정입니다.');
          return;
        }

        // 로그인 성공 → 회사코드 localStorage 저장
        localStorage.setItem('erp_tenant_code', tenantCode.trim().toUpperCase());
        router.replace('/dashboard');
      }
    } catch {
      setError('로그인 중 오류가 발생했습니다. 다시 시도해 주세요.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-muted/30 p-4">
      <Card className="w-full max-w-sm">
        <CardHeader className="text-center">
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img src="/logo-icon.svg" alt="비서단" className="mx-auto mb-2 size-16" />
          <CardTitle className="text-2xl font-bold text-primary">
            비서단 ERP
          </CardTitle>
          <CardDescription>
            계정에 로그인하세요
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="flex flex-col gap-4">
            <div className="flex flex-col gap-2">
              <Label htmlFor="tenantCode">회사코드</Label>
              <Input
                id="tenantCode"
                type="text"
                placeholder="예: SMARTCAST"
                value={tenantCode}
                onChange={(e) => {
                  setTenantCode(e.target.value.toUpperCase());
                  setTenantVerified(false);
                  setTenantError('');
                  setTenantName('');
                }}
                onBlur={() => verifyTenant(tenantCode)}
                required
                autoComplete="off"
                autoFocus
              />
              {tenantVerified && tenantName && (
                <p className="text-sm text-green-600">&quot;{tenantName}&quot; 확인됨</p>
              )}
              {tenantError && (
                <p className="text-sm text-destructive">{tenantError}</p>
              )}
            </div>

            <div className="flex flex-col gap-2">
              <Label htmlFor="email">이메일</Label>
              <Input
                id="email"
                type="email"
                placeholder="user@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
              />
            </div>

            <div className="flex flex-col gap-2">
              <Label htmlFor="password">비밀번호</Label>
              <Input
                id="password"
                type="password"
                placeholder="비밀번호 입력"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
              />
            </div>

            {error && (
              <p className="text-sm text-destructive" role="alert">
                {error}
              </p>
            )}

            <Button type="submit" className="w-full" disabled={isLoading || !tenantCode.trim()}>
              {isLoading ? '로그인 중...' : '로그인'}
            </Button>

            <p className="text-center text-sm text-muted-foreground">
              계정이 없으신가요?{' '}
              <Link href="/signup" className="text-primary hover:underline">
                회원가입
              </Link>
            </p>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
